import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, PoundSterling, Users, Calendar, CheckCircle2, ArrowRight, Handshake } from "lucide-react";
import ReferHostModal from "@/pages/referral/ReferHostModal";

const howItWorks = [
  { icon: Users, title: "Refer a host", desc: "Introduce a landlord or host to Truestays using your unique referral or by sending them our way." },
  { icon: Handshake, title: "They sign up", desc: "When your referral signs a management agreement and goes live, your referral is confirmed." },
  { icon: PoundSterling, title: "You get paid", desc: "Choose £250 cash, or up to 10% of their management fee for as long as they stay with us." },
];

const options = [
  {
    name: "Cash bonus",
    amount: "£250",
    desc: "A one-off £250 paid after your referral completes their 8th successful booking.",
    features: ["£250 one-off payment", "Paid after 8th successful booking", "No cap on referrals", "Available for every successful referral"],
    badge: "Most popular",
  },
  {
    name: "Ongoing fee share",
    amount: "Up to 10%",
    desc: "Earn up to 10% of the management fee we collect from your referral, paid quarterly for the first year of their agreement (average payment £47/month).",
    features: ["Up to 10% of management fee", "Paid quarterly", "Paid for the first year of the agreement", "Stacks across multiple referrals"],
    badge: "Best long-term value",
  },
];

const faqs = [
  { q: "How and when do I get paid?", a: "If you choose the cash bonus, you'll receive £250 once your referral completes their 8th successful booking. If you choose the ongoing fee share, you'll receive up to 10% of the management fee we collect, paid quarterly for as long as the referred host stays with Truestays." },
  { q: "Is there a limit to how many people I can refer?", a: "No. You can refer as many hosts and landlords as you like, and rewards stack - each successful referral earns you another bonus or another recurring fee share." },
  { q: "What counts as a successful referral?", a: "A referral is confirmed once the referred host signs a management agreement with Truestays and their listing goes live. The reward is then triggered according to the terms of the option you chose." },
  { q: "Can I choose a different option for each referral?", a: "Yes. You can select the cash bonus for one referral and the ongoing fee share for another - whichever suits you best." },
];

export default function Referral() {
  const [showModal, setShowModal] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "Refer a Landlord | Earn Cash Rewards | Truestays";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Refer a property owner to Truestays and earn a cash reward for every successful onboarding. No cap, no expiry. Free to join.");
    setMeta("robots", "index, follow");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://www.truestays.co.uk/referral";

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
    ld.dataset.dynamic = "referral-faq";
    document.head.appendChild(ld);
    return () => { document.head.removeChild(ld); };
  }, []);

  return (
    <div style={{ paddingTop: 68 }}>
      {/* Hero */}
      <section style={{ backgroundColor: "#f4f2f0", padding: "clamp(48px,6vw,88px) clamp(20px,5vw,80px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 56, alignItems: "center" }} className="ref-hero-grid">
          <div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", marginBottom: 16, marginTop: 0 }}>Referral Programme</p>
            <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(34px,4.5vw,56px)", color: "#1A1A1A", margin: "0 0 20px", letterSpacing: "-0.03em", lineHeight: 1.08 }}>
              Refer a host and earn £250 per property.
            </h1>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "clamp(16px,2vw,19px)", color: "#555", maxWidth: 480, margin: "0 0 28px", lineHeight: 1.65 }}>
              Know a landlord or host who'd benefit from Truestays? Introduce them and earn a reward when they successfully sign up. No cap - refer as many as you like.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button onClick={() => setShowModal(true)} style={{ background: "#112215", color: "#fff", border: "none", borderRadius: 10, padding: "15px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "background 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#000"}
                onMouseLeave={e => e.currentTarget.style.background = "#112215"}>
                Refer a host now <ArrowRight size={16} />
              </button>
              <Link to="/support" style={{ background: "transparent", color: "#1A1A1A", border: "1.5px solid #D5D2CE", borderRadius: 10, padding: "14px 24px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
                Ask a question
              </Link>
            </div>
          </div>
          <div style={{ position: "relative" }}>
            <div style={{ borderRadius: 24, overflow: "hidden", aspectRatio: "4 / 3" }}>
              <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/bbbb5766b_StreoAd-260x2003.jpg" alt="Truestays referral programme" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
            </div>
            <div style={{ position: "absolute", bottom: -20, left: -20, background: "#fff", borderRadius: 16, padding: "18px 22px", boxShadow: "0 16px 40px rgba(0,0,0,0.12)", border: "1px solid #EBEBEB" }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "#888", margin: "0 0 4px" }}>You earn</p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 28, color: "#1A1A1A", margin: 0 }}>£250 <span style={{ fontSize: 15, color: "#888", fontWeight: 500 }}>or 10%</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ backgroundColor: "#fff", padding: "clamp(56px,7vw,96px) clamp(20px,5vw,80px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>How it works</p>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(26px,3.5vw,40px)", letterSpacing: "-0.02em", color: "#1A1A1A", margin: "0 0 14px" }}>Three simple steps</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }} className="ref-steps-grid">
            {howItWorks.map(({ icon: Icon, title, desc }, i) => (
              <div key={title} style={{ background: "#F8F8F6", border: "1px solid #EBEBEB", borderRadius: 16, padding: "28px 24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: "#fff", border: "1px solid #EBEBEB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon size={20} color="#1A1A1A" />
                  </div>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 26, color: "#D5D2CE" }}>0{i + 1}</span>
                </div>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#1A1A1A", margin: "0 0 8px" }}>{title}</h3>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#666", lineHeight: 1.6, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reward options */}
      <section style={{ backgroundColor: "#F8F8F6", padding: "clamp(56px,7vw,96px) clamp(20px,5vw,80px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>Your reward</p>
            <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(26px,3.5vw,40px)", letterSpacing: "-0.02em", color: "#1A1A1A", margin: "0 0 14px" }}>Choose how you get paid</h2>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#666", maxWidth: 520, margin: "0 auto", lineHeight: 1.6 }}>Pick the reward that suits you - and choose again for every referral.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }} className="ref-options-grid">
            {options.map((opt) => (
              <div key={opt.name} style={{ background: "#fff", border: "1px solid #EBEBEB", borderRadius: 16, padding: "32px 28px", display: "flex", flexDirection: "column", position: "relative" }}>
                <div style={{ position: "absolute", top: -12, left: 28, background: "#112215", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, padding: "5px 14px", borderRadius: 100, letterSpacing: "0.04em" }}>{opt.badge}</div>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: "#1A1A1A", margin: "0 0 6px" }}>{opt.name}</h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 34, color: "#1A1A1A", margin: "0 0 12px", letterSpacing: "-0.02em" }}>{opt.amount}</p>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#666", lineHeight: 1.6, margin: "0 0 20px" }}>{opt.desc}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
                  {opt.features.map((f) => (
                    <div key={f} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <CheckCircle2 size={16} color="#1A1A1A" style={{ flexShrink: 0, marginTop: 1 }} />
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#444", lineHeight: 1.45 }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setShowModal(true)} style={{ marginTop: 24, background: "#112215", color: "#fff", border: "none", borderRadius: 8, padding: "13px 20px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#000"}
                  onMouseLeave={e => e.currentTarget.style.background = "#112215"}>
                  Claim this reward
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* T&Cs summary */}
      <section style={{ backgroundColor: "#fff", padding: "clamp(48px,6vw,80px) clamp(20px,5vw,80px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", background: "#F8F8F6", border: "1px solid #EBEBEB", borderRadius: 16, padding: "32px 40px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <Calendar size={20} color="#1A1A1A" />
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 18, color: "#1A1A1A", margin: 0 }}>How the reward is paid</h3>
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 32px" }} className="ref-tcs-list">
            {[
              "Cash bonus: paid after your referral completes their 8th successful booking.",
              "Ongoing fee share: up to 10% of the management fee, paid quarterly for the first year of the referred contract (average £47/month).",
              "A referral is confirmed once the host signs a management agreement and their listing goes live.",
              "No limit on the number of referrals - rewards stack.",
              "Truestays Group Ltd. (trading as Truestays) reserves the right to update these terms.",
            ].map((t) => (
              <li key={t} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <CheckCircle2 size={16} color="#1A1A1A" style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#444", lineHeight: 1.55 }}>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ backgroundColor: "#F8F8F6", padding: "clamp(48px,6vw,80px) clamp(20px,5vw,80px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(24px,3vw,34px)", letterSpacing: "-0.02em", color: "#1A1A1A", margin: "0 0 28px", textAlign: "center" }}>Referral FAQs</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} style={{ background: "#fff", border: "1px solid #EBEBEB", borderRadius: 12, overflow: "hidden" }}>
                  <button onClick={() => setOpenFaq(open ? null : i)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 15, color: "#1A1A1A" }}>{f.q}</span>
                    <span style={{ fontSize: 20, color: "#888", transition: "transform 0.2s", transform: open ? "rotate(45deg)" : "none" }}>+</span>
                  </button>
                  {open && <div style={{ padding: "0 22px 20px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#555", lineHeight: 1.65 }}>{f.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {showModal && <ReferHostModal onClose={() => setShowModal(false)} onSubmitted={() => setShowModal(false)} />}

      <style>{`
        @media (max-width: 900px) { .ref-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; } .ref-steps-grid { grid-template-columns: 1fr !important; } .ref-options-grid { grid-template-columns: 1fr !important; } .ref-tcs-list { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}